import React, {createContext, ReactNode, useContext} from 'react';

import {format, parseISO} from 'date-fns';

interface HelpersContextData {
  formatNumberToLocaleAmount: (value: number) => string;
  formatStringToLocaleAmount: (value: string) => string;
  formatLocaleAmountToNumber: (value: string) => number;
  formatDateAndTime: (value: string) => string;
  formatTime: (value: string) => string;
  ordenaCidadesOrdemAlfabetica: (data: any) => any;
  ordenaEstadosOrdemAlfabetica: (data: any) => any;
  formatDate: (value: string) => string;
  formataNomeEstado: (name: string) => string;
}
interface HelpersProviderProps {
  children: ReactNode;
}

const HelpersContext = createContext<HelpersContextData>(
  {} as HelpersContextData,
);

function HelpersProvider({children}: HelpersProviderProps) {
  function formatNumberToLocaleAmount(value: number) {
    const number = value;
    const hasDecimals = number % 1 !== 0;

    const roundedNumber = hasDecimals ? Math.ceil(number * 100) / 100 : number;

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(roundedNumber);
  }

  function formatStringToLocaleAmount(value: string) {
    const number = parseFloat(value.replace(',', '.'));
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number);
  }

  function formatLocaleAmountToNumber(value: string) {
    return Number(value.replace('R$', '').replace(',', '.'));
  }

  function formatDateAndTime(value: string) {
    let data = format(parseISO(value), 'dd/MM/yyyy');
    let time = format(parseISO(value), 'HH:mm');
    return data + ' às ' + time;
  }
  function formatDate(value: string) {
    let data = format(parseISO(value), 'dd/MM/yyyy');
    return data;
  }
  function formatTime(value: string) {
    let time = format(parseISO(value), 'HH:mm');
    return time;
  }

  function ordenaEstadosOrdemAlfabetica(data: any) {
    return data.sort((a: any, b: any) => {
      if (
        a._id.normalize('NFD').replace(/[\u0300-\u036f]/g, '') <
        b._id.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      ) {
        return -1;
      }
      if (
        a._id.normalize('NFD').replace(/[\u0300-\u036f]/g, '') >
        b._id.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      ) {
        return 1;
      }
      return 0;
    });
  }

  function formataNomeEstado(name: string) {
    let estado: string;
    switch (name) {
      case 'AC':
        estado = 'Acre';
        break;
      case 'AL':
        estado = 'Alagoas';
        break;
      case 'AP':
        estado = 'Amapá';
        break;
      case 'AM':
        estado = 'Amazonas';
        break;
      case 'BA':
        estado = 'Bahia';
        break;
      case 'CE':
        estado = 'Ceará';
        break;
      case 'DF':
        estado = 'Distrito Federal';
        break;
      case 'ES':
        estado = 'Espírito Santo';
        break;
      case 'GO':
        estado = 'Goiás';
        break;
      case 'MA':
        estado = 'Maranhão';
        break;
      case 'MT':
        estado = 'Mato Grosso';
        break;
      case 'MS':
        estado = 'Mato Grosso do Sul';
        break;
      case 'MG':
        estado = 'Minas Gerais';
        break;
      case 'PA':
        estado = 'Pará';
        break;
      case 'PB':
        estado = 'Paraíba';
        break;
      case 'PR':
        estado = 'Paraná';
        break;
      case 'PE':
        estado = 'Pernambuco';
        break;
      case 'PI':
        estado = 'Piauí';
        break;
      case 'RJ':
        estado = 'Rio de Janeiro';
        break;
      case 'RN':
        estado = 'Rio Grande do Norte';
        break;
      case 'RS':
        estado = 'Rio Grande do Sul';
        break;
      case 'RO':
        estado = 'Rondônia';
        break;
      case 'RR':
        estado = 'Roraima';
        break;
      case 'SC':
        estado = 'Santa Catarina';
        break;
      case 'SP':
        estado = 'São Paulo';
        break;
      case 'SE':
        estado = 'Sergipe';
        break;
      case 'TO':
        estado = 'Tocantins';
        break;
      default:
        estado = name;
        break;
    }

    return estado;
  }

  function ordenaCidadesOrdemAlfabetica(data: any) {
    return data.sort((a: any, b: any) => {
      if (
        a.cidade.normalize('NFD').replace(/[\u0300-\u036f]/g, '') <
        b.cidade.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      ) {
        return -1;
      }
      if (
        a.cidade.normalize('NFD').replace(/[\u0300-\u036f]/g, '') >
        b.cidade.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      ) {
        return 1;
      }
      return 0;
    });
  }

  return (
    <HelpersContext.Provider
      value={{
        formatNumberToLocaleAmount,
        formatDateAndTime,
        ordenaCidadesOrdemAlfabetica,
        ordenaEstadosOrdemAlfabetica,
        formatDate,
        formatTime,
        formataNomeEstado,
        formatStringToLocaleAmount,
        formatLocaleAmountToNumber,
      }}>
      {children}
    </HelpersContext.Provider>
  );
}

function useHelpers() {
  const context = useContext(HelpersContext);
  return context;
}

export {useHelpers, HelpersProvider};
